import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, type FormControlStatus, FormGroup, Validators } from '@angular/forms';

import moment from 'moment-timezone';

import {
  DynamicFormRespondComponent,
  RespondSelectSingleComponent,
} from '@core/ask-hr-dynamic-forms-smart-ui';
import type {
  DynamicFormAnswerConfig,
  DynamicFormConfig,
  RespondFormState,
} from '@core/ask-hr-dynamic-forms-types';
import { PriorityService } from '@core/ask-hr-templates-logic';
import type { AskTemplate } from '@core/ask-hr-templates-types';
import type { CreateAskTicket } from '@core/ask-hr-tickets-types';
import type { AskTicketPriority } from '@core/ask-hr-types';
import { formSignalStatus, formSignalValue } from '@core/common-legacy';
import { ProsemirrorBasicConfig } from '@core/prosemirror-legacy/webapp';
import { ProtectedControlDirective } from '@core/user-interface-legacy';

import { AskTemplateCardComponent } from '../ask-templates-dialog/ask-template-card/ask-template-card.component';
import {
  FormKindProviders,
  AskHrDynamicFormConfig,
  AskTemplatesFormService,
  type AskTemplateFormState,
} from '../ask-templates-form.service';

@Component({
  selector: 'tlc-create-ticket',
  imports: [
    RespondSelectSingleComponent,
    DynamicFormRespondComponent,
    AskTemplateCardComponent,
    ProtectedControlDirective,
  ],
  template: `
    <ask-template-card [askTemplate]="template()" margin-bottom-medium />
    @if (template().prioritySelectionEnabled) {
      <input-radio-respond
        [respondConfig]="priorityConfig"
        [respondForm]="priorityControl"
        [rtProtectedControl]="priorityControl"
        margin-bottom-medium
      />
    }
    <dynamic-form-respond
      [formConfig]="respondFormConfig()"
      (formStateChanged)="onRespondFormValue($event)"
      (formStatusChanged)="respondFormStatus.set($event)"
    />
  `,
  styleUrl: './create-ticket.component.less',
  providers: [...FormKindProviders, [ProsemirrorBasicConfig, AskTemplatesFormService]],
})
export class CreateTicketComponent {
  private readonly askTemplatesFormService = inject(AskTemplatesFormService);

  public template = input.required<AskTemplate>();
  public ticketChanges = output<CreateAskTicket<AskHrDynamicFormConfig>>();

  public isFormValid = computed((): boolean => {
    const { prioritySelectionEnabled } = this.template();
    const respondFormValid = this.respondFormStatus().every(status => status === 'VALID');
    const isFormValid = prioritySelectionEnabled
      ? respondFormValid && this.priorityStatus() === 'VALID'
      : respondFormValid;
    return isFormValid;
  });

  protected dynamicFormRespondComponent = viewChild.required(DynamicFormRespondComponent);
  protected priorityControl = new FormGroup({
    value: new FormControl<AskTicketPriority | null>(null, {
      nonNullable: true,
      validators: Validators.required,
    }),
  });
  protected priorityStatus = formSignalStatus(this.priorityControl);
  protected priorityValue = formSignalValue(this.priorityControl);
  protected priorityConfig = inject(PriorityService).getPriorityConfig({ isDisabled: false });

  protected defineFormState = signal<AskTemplateFormState[]>([]);
  protected respondFormStatus = signal<FormControlStatus[]>([]);
  protected respondFormConfig = computed((): DynamicFormAnswerConfig[] =>
    this.defineFormState().map(item => ({
      kind: item.kind,
      config: item.value,
      hash: item.hash,
    })),
  );
  protected respondFormValue = signal<Array<RespondFormState<AskHrDynamicFormConfig>>>([]);

  constructor() {
    effect(() => this.watchTicketForm());
  }

  public ngOnInit(): void {
    this.defineFormState.set(
      (this.template().form.items ?? []).map(item =>
        this.askTemplatesFormService.askTemplateFromDto(item),
      ),
    );
  }

  public resetFormStatus() {
    this.priorityControl.markAsPristine();
    this.priorityControl.markAsUntouched();
    this.dynamicFormRespondComponent().resetFromStatus();
  }

  protected onRespondFormValue(state: Array<RespondFormState<DynamicFormConfig>>) {
    this.respondFormValue.set(<Array<RespondFormState<AskHrDynamicFormConfig>>>state);
  }

  private watchTicketForm() {
    const respondFormState = this.respondFormValue();
    const templateId = this.template().id;
    const priority = this.priorityValue().value ?? null;

    const form = {
      id: templateId,
      priority,
      items: respondFormState
        .filter(item => item.kind !== 'infobox')
        .map(item => {
          if (item.kind === 'date') {
            return item.value.value
              ? {
                  ...item,
                  value: {
                    ...item.value,
                    value: moment(item.value.value).format('YYYY-MM-DD'),
                  },
                }
              : item;
          }

          return item;
        }),
    } satisfies CreateAskTicket<AskHrDynamicFormConfig>;

    this.ticketChanges.emit(form);
  }
}
